export interface BlogPost {
	id: number;
	slug: string;
	category: string;
	title: string;
	excerpt: string;
	date: string;
	readTime: string;
	gradient: string;
	number: string;
	content: BlogSection[];
}

export interface BlogSection {
	type: 'paragraph' | 'heading' | 'list' | 'quote';
	text?: string;
	items?: string[];
}

export const blogPosts: BlogPost[] = [
	{
		id: 1,
		slug: 'wplyw-aktywnosci-fizycznej-na-zdrowie-psychiczne',
		category: 'TRENING',
		title: 'Wpływ aktywności fizycznej na zdrowie psychiczne',
		excerpt: 'Regularna aktywność fizyczna to jeden z najskuteczniejszych sposobów na poprawę samopoczucia. W dzisiejszym świecie, gdzie stres i napięcia towarzyszą nam niemal codziennie, ruch staje się lekiem na wyciągnięcie ręki.',
		date: 'Marzec 2024',
		readTime: '5 min',
		gradient: 'from-primary-500 to-primary-700',
		number: '01',
		content: [
			{
				type: 'paragraph',
				text: 'Żyjemy w świecie, w którym stres jest niemal stałym towarzyszem. Praca, obowiązki, tempo życia — to wszystko odbija się na naszym samopoczuciu. I choć na rynku nie brakuje różnych metod radzenia sobie z napięciem, nauka od lat wskazuje na jedno z najprostszych i najskuteczniejszych rozwiązań: regularny ruch.',
			},
			{
				type: 'heading',
				text: 'Co dzieje się w mózgu podczas ćwiczeń?',
			},
			{
				type: 'paragraph',
				text: 'Podczas aktywności fizycznej mózg wydziela endorfiny — neuroprzekaźniki nazywane potocznie "hormonami szczęścia". To właśnie one odpowiadają za uczucie euforii, które często odczuwasz po dobrym treningu. Ale to nie wszystko. Ćwiczenia stymulują również produkcję serotoniny i dopaminy, substancji kluczowych dla regulacji nastroju, motywacji i poczucia nagrody.',
			},
			{
				type: 'paragraph',
				text: 'Badania pokazują, że już 30 minut umiarkowanej aktywności fizycznej może znacząco obniżyć poziom kortyzolu — hormonu stresu — we krwi. Efekt utrzymuje się przez kilka godzin po treningu, co przekłada się na spokojniejszy wieczór i lepszy sen.',
			},
			{
				type: 'heading',
				text: 'Aktywność fizyczna a depresja i lęk',
			},
			{
				type: 'paragraph',
				text: 'Metaanalizy obejmujące dziesiątki tysięcy uczestników konsekwentnie potwierdzają: regularne ćwiczenia są porównywalne w skuteczności z terapią farmakologiczną w łagodnych i umiarkowanych przypadkach depresji. Co ważne, nie masz tu efektów ubocznych — wprost przeciwnie, zyskujesz lepszą kondycję, sylwetkę i pewność siebie.',
			},
			{
				type: 'paragraph',
				text: 'W przypadku zaburzeń lękowych ruch działa jak naturalny "reset" układu nerwowego. Aktywuje przywspółczulny układ nerwowy odpowiedzialny za stan spokoju i regeneracji, redukując nadmierną czujność i napięcie mięśniowe charakterystyczne dla chronicznego lęku.',
			},
			{
				type: 'heading',
				text: 'Jakie formy ruchu pomagają najbardziej?',
			},
			{
				type: 'list',
				items: [
					'Trening siłowy — buduje poczucie sprawczości i pewność siebie, wyraźnie obniża objawy depresji',
					'Cardio o umiarkowanej intensywności (spacer, rower, pływanie) — świetne na redukcję stresu i poprawę jakości snu',
					'Joga i pilates — łączą ruch z oddechem, wyjątkowo skuteczne przy stanach lękowych',
					'Aktywność na świeżym powietrzu — kontakt z naturą wzmacnia efekty psychiczne ruchu',
				],
			},
			{
				type: 'quote',
				text: 'Nie musisz od razu biegać maratonów. Zacznij od 3 treningów w tygodniu po 30–40 minut. To wystarczy, żeby zauważyć realną różnicę w samopoczuciu już po 2–3 tygodniach.',
			},
			{
				type: 'heading',
				text: 'Praktyczne wskazówki na start',
			},
			{
				type: 'list',
				items: [
					'Wybierz formę aktywności, która sprawia Ci przyjemność — masz większą szansę, że się przy niej utrzymasz',
					'Trenuj regularnie, nie intensywnie na początku — regularność jest ważniejsza niż intensywność',
					'Ćwicz o stałej porze — łatwiej zbudować nawyk, gdy trening jest częścią rutyny dnia',
					'Jeśli możesz, trenuj z kimś — towarzystwo zwiększa motywację i poprawia nastrój',
					'Śledź swoje postępy — nawet mała poprawa widoczna na papierze daje dużo satysfakcji',
				],
			},
			{
				type: 'paragraph',
				text: 'Pamiętaj — ruch to inwestycja nie tylko w ciało, ale przede wszystkim w głowę. Jeśli nie wiesz, od czego zacząć, chętnie Ci pomogę ułożyć plan dopasowany do Twoich potrzeb i możliwości. Zaczynamy tam, gdzie jesteś — i idziemy po więcej.',
			},
		],
	},
	{
		id: 2,
		slug: 'trening-interwalowy-30-minut',
		category: 'TRENING',
		title: 'Trening interwałowy – 30 minut dla zdrowia i efektywności',
		excerpt: 'HIIT to jedna z najbardziej efektywnych metod treningowych. W zaledwie 30 minut osiągniesz znakomite wyniki i spalisz więcej kalorii niż podczas godziny tradycyjnego cardio.',
		date: 'Kwiecień 2024',
		readTime: '4 min',
		gradient: 'from-rose-500 to-primary-600',
		number: '02',
		content: [
			{
				type: 'paragraph',
				text: 'Jedną z najczęstszych wymówek, jeśli chodzi o brak aktywności fizycznej, jest brak czasu. I rozumiem to doskonale — nasze życia są napięte, a doba ma tylko 24 godziny. Właśnie dlatego warto poznać trening interwałowy wysokiej intensywności, znany jako HIIT (High-Intensity Interval Training).',
			},
			{
				type: 'heading',
				text: 'Czym właściwie jest HIIT?',
			},
			{
				type: 'paragraph',
				text: 'HIIT polega na naprzemiennym wykonywaniu ćwiczeń o wysokiej intensywności i krótkich przerwach odpoczynku lub ćwiczeń o niższej intensywności. Klasyczny schemat to np. 40 sekund wysiłku maksymalnego, 20 sekund odpoczynku — powtarzane przez 20–30 minut. Brzmi prosto? Bo jest. Ale efekty są spektakularne.',
			},
			{
				type: 'heading',
				text: 'Dlaczego HIIT działa tak dobrze?',
			},
			{
				type: 'list',
				items: [
					'Efekt "afterburn" (EPOC) — po treningu ciało spala kalorie jeszcze przez kilkanaście godzin',
					'Poprawia wrażliwość na insulinę i gospodarkę cukrową',
					'Zwiększa wydolność sercowo-naczyniową szybciej niż tradycyjne cardio',
					'Chroni masę mięśniową lepiej niż długotrwałe bieganie w stałym tempie',
					'Zajmuje 30 minut zamiast 60 — i daje lepsze wyniki',
				],
			},
			{
				type: 'heading',
				text: 'Przykładowy trening HIIT dla początkujących (30 min)',
			},
			{
				type: 'paragraph',
				text: 'Rozgrzewka (5 minut): marsz w miejscu, krążenia ramion, delikatne przysiady bez obciążenia. Część główna (20 minut, 4 rundy po 5 minut): jumping jacks 40 s / przerwa 20 s, przysiady 40 s / przerwa 20 s, pompki (lub wersja na kolanach) 40 s / przerwa 20 s, mountain climbers 40 s / przerwa 20 s. Schłodzenie (5 minut): rozciąganie statyczne całego ciała.',
			},
			{
				type: 'quote',
				text: 'Kluczem do HIIT jest pełne zaangażowanie podczas faz wysiłku. Te 40 sekund powinno być naprawdę intensywne — nie biegaj, a sprintuj. Nie przysiądź, a atakuj ruch z pełną mocą.',
			},
			{
				type: 'heading',
				text: 'Dla kogo HIIT jest odpowiedni?',
			},
			{
				type: 'paragraph',
				text: 'HIIT to świetna opcja dla osób z napiętym grafikiem, które chcą efektywnie spalać tkankę tłuszczową i poprawiać kondycję. Jednak nie jest to trening dla każdego na każdym etapie. Jeśli dopiero zaczynasz przygodę z aktywnością fizyczną lub masz problemy ze stawami, zacznij od spokojniejszych form ruchu i stopniowo buduj bazę kondycyjną.',
			},
			{
				type: 'paragraph',
				text: 'Ważna jest też regeneracja — HIIT nie powinien być wykonywany częściej niż 3 razy w tygodniu, najlepiej z co najmniej jednym dniem przerwy między sesjami. Jeśli masz wątpliwości, czy taki trening jest dla Ciebie odpowiedni, skonsultuj się ze mną — ułożę plan dopasowany do Twojego poziomu i celów.',
			},
		],
	},
	{
		id: 3,
		slug: 'dieta-a-trening-co-jesc',
		category: 'DIETA',
		title: 'Dieta a trening – co jeść, żeby osiągać wyniki?',
		excerpt: 'Prawidłowe odżywianie to fundament każdego planu treningowego. Nie wystarczy ciężko trenować – musisz też zadbać o odpowiednie paliwo dla swojego ciała.',
		date: 'Maj 2024',
		readTime: '6 min',
		gradient: 'from-accent-500 to-accent-700',
		number: '03',
	content: [
			{
				type: 'paragraph',
				text: 'Jednym z najczęstszych błędów osób zaczynających trenować jest skupienie się wyłącznie na ćwiczeniach i zaniedbanie diety. Tymczasem żywienie to dosłownie fundament, na którym budujesz swoje rezultaty. Mówi się, że efekty to 20% trening i 80% kuchnia — i choć to uproszczenie, zawiera dużo prawdy.',
			},
			{
				type: 'heading',
				text: 'Trzy makroskładniki, które musisz znać',
			},
			{
				type: 'list',
				items: [
					'Białko — budulec mięśni, klucz do regeneracji. Cel: 1,6–2,2 g na kg masy ciała dziennie. Źródła: kurczak, jaja, ryby, twaróg, rośliny strączkowe.',
					'Węglowodany — główne paliwo dla mięśni i mózgu. Nie bój się ich! Wybieraj złożone: kasze, ryż, ziemniaki, pełnoziarniste pieczywo.',
					'Tłuszcze — niezbędne do produkcji hormonów (w tym testosteronu i estrogenów), wchłaniania witamin. Źródła: awokado, oliwa z oliwek, orzechy, tłuste ryby.',
				],
			},
			{
				type: 'heading',
				text: 'Co jeść przed treningiem?',
			},
			{
				type: 'paragraph',
				text: 'Posiłek przedtreningowy powinien dostarczyć energię na wysiłek, nie obciążając jednocześnie układu pokarmowego. Optymalnie zjedz go 1,5–2 godziny przed treningiem. Dobra propozycja to ryż lub kasza z chudym białkiem (np. pierś z kurczaka lub jajka) i warzywa. Unikaj dużych ilości tłuszczu i błonnika tuż przed ćwiczeniami — spowalniają trawienie i mogą powodować dyskomfort.',
			},
			{
				type: 'heading',
				text: 'Co jeść po treningu?',
			},
			{
				type: 'paragraph',
				text: 'Okno anaboliczne — czas zaraz po treningu — to moment, w którym Twoje mięśnie są wyjątkowo chłonne na składniki odżywcze. Celuj w posiłek bogaty w białko i węglowodany w ciągu 1–2 godzin po wysiłku. Świetna opcja to koktajl białkowy z bananem, twaróg z owocami i płatkami owsianymi albo klasyczny ryż z kurczakiem i warzywami.',
			},
			{
				type: 'quote',
				text: 'Nie musisz od razu liczyć każdej kalorii. Zacznij od prostych zasad: jedz więcej białka, ogranicz przetworzoną żywność i zadbaj o regularność posiłków. To wystarczy, żeby zobaczyć pierwsze efekty.',
			},
			{
				type: 'heading',
				text: 'Nawodnienie — często zapominany element',
			},
			{
				type: 'paragraph',
				text: 'Nawet lekkie odwodnienie (już 1–2% ubytku masy ciała w postaci wody) znacząco obniża wydolność i zdolność koncentracji. Pij regularnie przez cały dzień — nie czekaj aż poczujesz pragnienie. Orientacyjna norma to 2–3 litry wody dziennie, więcej w dni treningowe i upalne.',
			},
			{
				type: 'heading',
				text: 'Praktyczne wskazówki dla kobiet trenujących siłowo',
			},
			{
				type: 'list',
				items: [
					'Nie bój się jeść — trening siłowy zwiększa zapotrzebowanie kaloryczne. Niedojadanie hamuje postępy i osłabia hormony',
					'Śledź cykl menstruacyjny — w drugiej fazie cyklu (lutealna) zapotrzebowanie kaloryczne jest wyższe o ok. 100–300 kcal',
					'Suplementacja: witamina D3 + K2, magnez i żelazo to trójka, którą polecam większości moich podopiecznych',
					'Jedz regularnie — 3–4 posiłki dziennie to dobra baza, która utrzymuje równy poziom cukru we krwi',
					'Gotuj z wyprzedzeniem — meal prep na 2–3 dni naprzód eliminuje pokusy złego jedzenia w chwilach zmęczenia',
				],
			},
			{
				type: 'paragraph',
				text: 'Dieta to temat rzeka i każda z nas jest inna — inne preferencje smakowe, metabolizm, styl życia. Jeśli chcesz mieć plan żywieniowy skrojony pod siebie, napisz do mnie. Razem ułożymy coś, co będziesz mogła realnie stosować — bez głodzenia i wyrzeczeń.',
			},
		],
	},
];
